import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

interface TrialOfferDialogProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

export const TrialOfferDialog: React.FC<TrialOfferDialogProps> = ({
  open,
  onAccept,
  onDecline,
}) => {
  const { theme } = useTheme();

  const features = [
    '10 eventos por mês',
    '50 convidados',
    '30 receitas',
    '100 itens no acervo',
    '10 grupos de convidados',
    '5 listas de compras',
  ];

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      PaperProps={{
        sx: {
          borderRadius: theme.radii.large,
          backgroundColor: theme.colors.background,
        },
      }}
    >
      <DialogContent sx={{ pt: 3, pb: 1.5 }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: theme.colors.primary,
              mb: 0.5,
            }}
          >
            Bem-vindo ao Hostly
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
              color: theme.colors.text.primary,
              mb: 0.5,
              fontSize: '1rem',
            }}
          >
            Experimente 30 dias grátis do Plano Basic
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.colors.text.secondary,
              fontSize: '0.8rem',
            }}
          >
            Teste todas as funcionalidades sem compromisso
          </Typography>
        </Box>

        <List sx={{ mb: 1.5, py: 0 }}>
          {features.map((feature, index) => (
            <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckIcon sx={{ color: theme.colors.primary, fontSize: '1rem' }} />
              </ListItemIcon>
              <ListItemText
                primary={feature}
                primaryTypographyProps={{
                  sx: { color: theme.colors.text.primary, fontSize: '0.8rem' },
                }}
              />
            </ListItem>
          ))}
        </List>

        <Box
          sx={{
            backgroundColor: `${theme.colors.primary}10`,
            borderRadius: theme.radii.medium,
            p: 1.5,
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.colors.text.primary,
              textAlign: 'center',
              fontSize: '0.7rem',
            }}
          >
            Após 30 dias, você pode continuar no plano Free (1 evento/mês) ou
            assinar os planos Basic ou Pro
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onDecline}
          variant="outlined"
          fullWidth
          sx={{
            borderColor: theme.colors.border,
            color: theme.colors.text.secondary,
            '&:hover': {
              borderColor: theme.colors.border,
              backgroundColor: `${theme.colors.border}20`,
            },
          }}
        >
          Usar Free agora
        </Button>
        <Button
          onClick={onAccept}
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: theme.colors.primary,
            color: theme.colors.text.contrast,
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: theme.colors.primary,
              opacity: 0.9,
            },
          }}
        >
          Começar período gratuito de 30 dias
        </Button>
      </DialogActions>
    </Dialog>
  );
};
